/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * P-5 (S629) — the shop's translation engine.
 *
 * What this file used to be: the react-i18next sample, holding two "Welcome to
 * React" strings and nothing else. It was already imported by main.tsx, so the
 * engine already started before the first render — the wiring was in place and
 * only the contents were a placeholder. This replaces the contents. It does NOT
 * add a second i18n file beside it, because two engines is how two parts of one
 * page end up in two languages.
 *
 * ONE LANGUAGE IS LOADED, NOT SIX. English is imported statically because it is
 * the fallback: a key missing from a translation must show words rather than a
 * key name. Every other language is fetched on demand with a dynamic import, so
 * a customer downloads the roughly 90 KB of their own language instead of the
 * roughly 560 KB of all six, five sixths of which no single customer ever reads.
 * This is the ordinary way it is done, and it removes the question of bundle
 * growth rather than answering it.
 *
 * KEYS ARE FLAT-PATHED, NOT NAMESPACED. `keySeparator: "."` with a single
 * `translation` namespace means `t("dashboard.products.title")` addresses the
 * supplied files exactly as they are shaped, with no namespace juggling.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "@/locales/en.json";
import {
  DEFAULT_LANGUAGE,
  isSupportedLang,
  toSupportedLang,
  type SupportedLanguage,
} from "@/lib/supported-languages";
import { formatNumber } from "@/lib/active-locale";
import { readStoredLanguage } from "@/lib/lang-storage";
import { langFromPath } from "@/lib/localized-path";

/**
 * The language to start in: the address first, storage second, English last.
 * The address wins because a customer following a `/de/cart` link must get a
 * German cart even if they once chose Spanish on this machine.
 */
function initialLanguage(): SupportedLanguage {
  try {
    const fromPath = langFromPath(window.location.pathname);
    if (fromPath) return fromPath;
  } catch {
    /* no window (tests, SSR-style tooling) — fall through */
  }
  return readStoredLanguage() ?? DEFAULT_LANGUAGE;
}

/** Language files already fetched, so a switch back does not fetch twice. */
const loaded = new Set<string>([DEFAULT_LANGUAGE]);

/**
 * Fetch a language's file and register it, then make it active.
 *
 * Failure is not fatal on purpose. If the chunk cannot be fetched — a flaky
 * connection, a stale deployment — the shop stays in the language it is already
 * showing rather than rendering raw key names at the customer.
 */
export async function loadLanguage(lang: unknown): Promise<SupportedLanguage> {
  const target = toSupportedLang(lang);

  if (!loaded.has(target)) {
    try {
      const module = await import(`./locales/${target}.json`);
      i18n.addResourceBundle(
        target,
        "translation",
        module.default ?? module,
        true,
        true,
      );
      loaded.add(target);
    } catch {
      return (i18n.language as SupportedLanguage) ?? DEFAULT_LANGUAGE;
    }
  }

  if (i18n.language !== target) {
    await i18n.changeLanguage(target);
  }
  return target;
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
  },
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...(["en", "de", "fr", "es", "pt", "it"] as const)],
  defaultNS: "translation",
  ns: ["translation"],
  keySeparator: ".",
  nsSeparator: false,
  returnNull: false,
  interpolation: {
    // React escapes for us; escaping again turns an apostrophe in a French
    // string into `&#39;` on the screen.
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

/*
 * C1 (FCR S687): numbers are formatted BY THE LOCALE STRING, not by the caller.
 *
 * Eleven call sites passed `count: formatNumber(n)` -- a STRING -- into
 * i18next. `count` is not an ordinary interpolation value: i18next uses it to
 * select the plural form, and it converts what it is given to a number to do
 * that. `Number("1,000")` is NaN, which every language categorises as `other`.
 *
 * Nothing went wrong TODAY only because `common.wordCount` and
 * `common.wordCountOne` are separate keys with no `_one` / `_other` suffixes, so
 * i18next falls back to the base key whatever category it computed. The moment
 * anyone writes `wordCount_one` and `wordCount_other` -- the natural thing for a
 * German or French translator to do, and something this codebase already does in
 * eight other places -- every count of a thousand or more silently takes the
 * wrong plural form. A count of 1 still works, because `Number("1")` is 1, so it
 * would pass every small-number test anybody wrote.
 *
 * So the callers pass raw numbers and the locale strings say `{{count, number}}`.
 *
 * TWO THINGS ABOUT THE MECHANISM, both of which matter:
 *
 * 1. `interpolation.format` is NOT used. i18next's own documentation states the
 *    legacy format function is being removed, and that the built-in formatter is
 *    now always used and should be EXTENDED rather than replaced. Setting
 *    `interpolation.format` replaces the built-in formatter wholesale, so
 *    currency, datetime, list and relativetime would all start returning
 *    String(value) the moment anyone used one.
 * 2. It must be added AFTER init(), which is where the formatter service exists.
 *
 * Why route through formatNumber() at all, when i18next has a `number`
 * formatter of its own: because they can disagree. i18next formats in its
 * RESOLVED language; formatNumber() formats in the shop's ACTIVE locale, which
 * comes from the address and then the stored preference, and maps Portuguese to
 * pt-BR deliberately. src/lib/active-locale.ts exists precisely so the shop has
 * one answer to that question instead of four, and leaving the built-in in place
 * would quietly reintroduce a second one.
 */
i18n.services.formatter?.add("number", (value) =>
  typeof value === "number" ? formatNumber(value) : String(value),
);

// The chosen language may not be English, in which case its file has not been
// fetched yet. Starting the fetch here — rather than waiting for the first
// component to ask — means the swap happens within the first paint instead of
// after a visible flash of English.
const startup = initialLanguage();
if (isSupportedLang(startup) && startup !== DEFAULT_LANGUAGE) {
  void loadLanguage(startup);
}

export default i18n;
